import React, { useMemo, useRef, useState } from "react";
import type {
  SurveyAnswers,
  SurveyDefinition,
  SurveyQuestion,
  SurveyWave,
} from "../../hooks/useSurveyStatus";

interface SurveyFormProps {
  definition: SurveyDefinition;
  wave: SurveyWave;
  initialAnswers: SurveyAnswers;
  scheduleText: string;
  thankYouText: string;
  readOnly?: boolean;
  readOnlyMessage?: string;
  editing?: boolean;
  prefilled?: boolean;
  // eslint-disable-next-line no-unused-vars -- TypeScript callback parameter has no runtime binding.
  onSubmit: (answers: SurveyAnswers) => Promise<void>;
  onBackToCourses: () => void;
}

const OTHER_QUESTION_IDS = new Set(["q2", "q4"]);
const PERSONAL_DETAILS_WARNING =
  "Please don't include names or personal details about pupils, colleagues or parents";

const questionNumber = (question: SurveyQuestion) =>
  Number(question.id.replace(/^q/, ""));

const hasValue = (value: SurveyAnswers[string] | undefined) =>
  Array.isArray(value)
    ? value.length > 0
    : value !== undefined && value !== null && value !== "";

const valuesEqual = (left: string | number, right: string | number) =>
  left === right;

const optionLabel = (question: SurveyQuestion, value: string | number) =>
  question.options?.find((option) => valuesEqual(option.value, value))?.label;

const usesOther = (question: SurveyQuestion, value: SurveyAnswers[string] | undefined) => {
  if (!OTHER_QUESTION_IDS.has(question.id)) {return false;}
  const other = question.options?.find((option) => option.label === "Other");
  if (!other) {return false;}
  return Array.isArray(value)
    ? value.some((item) => valuesEqual(item, other.value))
    : value !== undefined && !Array.isArray(value) && valuesEqual(value, other.value);
};

const privacyParagraph = (text: string) => {
  const words = "privacy notice";
  const index = text.indexOf(words);
  if (index === -1) {return text;}
  return (
    <>
      {text.slice(0, index)}
      <a
        className="font-semibold text-primary underline underline-offset-2"
        href="https://pedagogue.io/privacy-policy/"
      >
        {words}
      </a>
      {text.slice(index + words.length)}
    </>
  );
};

export const SurveyForm: React.FC<SurveyFormProps> = ({
  definition,
  wave,
  initialAnswers,
  scheduleText,
  thankYouText,
  readOnly = false,
  readOnlyMessage = "This response is final because the 24-hour editing window has closed.",
  editing = false,
  prefilled = false,
  onSubmit,
  onBackToCourses,
}) => {
  const [answers, setAnswers] = useState<SurveyAnswers>({ ...initialAnswers });
  const [partIndex, setPartIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const questionRefs = useRef<Record<string, HTMLFieldSetElement | HTMLDivElement | null>>({});

  const questions = useMemo(() => {
    const form = definition.waveForms[wave];
    return [...definition.questions, ...definition.scenarioForms[form]].sort(
      (left, right) => questionNumber(left) - questionNumber(right),
    );
  }, [definition, wave]);

  const isVisible = (question: SurveyQuestion, currentAnswers: SurveyAnswers) => {
    if (question.id === "q2" && [1, 4].some((value) => valuesEqual(currentAnswers.q1 as string | number, value))) {
      return false;
    }
    if (question.id === "q4" && valuesEqual(currentAnswers.q3 as string | number, 0)) {
      return false;
    }
    if (question.id === "q24") {return wave === "start";}
    if (question.id === "q25") {return wave !== "start";}
    return true;
  };

  const visibleQuestions = questions.filter((question) => isVisible(question, answers));
  const visibleParts = definition.parts.filter((part) =>
    visibleQuestions.some((question) => question.part === part.id),
  );
  const safePartIndex = Math.min(partIndex, Math.max(visibleParts.length - 1, 0));
  const part = visibleParts[safePartIndex];
  const partQuestions = visibleQuestions.filter((question) => question.part === part?.id);
  const isFinalPart = safePartIndex === visibleParts.length - 1;

  const updateAnswer = (question: SurveyQuestion, value: SurveyAnswers[string]) => {
    setAnswers((current) => {
      const next = { ...current, [question.id]: value };
      if (question.id === "q1" && [1, 4].some((item) => valuesEqual(value as string | number, item))) {
        delete next.q2;
        delete next.q2_other;
      }
      if (question.id === "q2" && !usesOther(question, value)) {delete next.q2_other;}
      if (question.id === "q3" && valuesEqual(value as string | number, 0)) {
        delete next.q4;
        delete next.q4_other;
      }
      if (question.id === "q4" && !usesOther(question, value)) {delete next.q4_other;}
      return next;
    });
    setError(null);
  };

  const firstMissing = (questionsToCheck: SurveyQuestion[]) =>
    questionsToCheck.find((question) => {
      if (question.kind === "text") {return false;}
      if (!hasValue(answers[question.id])) {return true;}
      return usesOther(question, answers[question.id]) &&
        !String(answers[`${question.id}_other`] ?? "").trim();
    });

  const showMissing = (question: SurveyQuestion) => {
    setError(`Please answer ${question.id.toUpperCase()}: ${question.prompt}`);
    window.requestAnimationFrame(() => questionRefs.current[question.id]?.focus());
  };

  const continueToNextPart = () => {
    if (readOnly) {
      setError(null);
      setPartIndex(safePartIndex + 1);
      window.scrollTo?.(0, 0);
      return;
    }
    const missing = firstMissing(partQuestions);
    if (missing) {
      showMissing(missing);
      return;
    }
    setError(null);
    setPartIndex(safePartIndex + 1);
    window.scrollTo?.(0, 0);
  };

  const cleanAnswers = () => {
    const outgoing: SurveyAnswers = {};
    questions.filter((question) => isVisible(question, answers)).forEach((question) => {
      const value = answers[question.id];
      if (value !== undefined) {outgoing[question.id] = value;}
      const otherKey = `${question.id}_other`;
      if (usesOther(question, value) && answers[otherKey] !== undefined) {
        outgoing[otherKey] = String(answers[otherKey]).trim();
      }
    });
    return outgoing;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (readOnly || pending) {return;}
    if (!isFinalPart) {continueToNextPart();return;}
    const missing = firstMissing(visibleQuestions);
    if (missing) {
      const missingPartIndex = visibleParts.findIndex((item) => item.id === missing.part);
      setPartIndex(missingPartIndex);
      showMissing(missing);
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onSubmit(cleanAnswers());
      setSubmitted(true);
    } catch (submissionError) {
      setError(
        submissionError instanceof Error && submissionError.message
          ? submissionError.message
          : "We couldn't save your answers. Please try again.",
      );
    } finally {
      setPending(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background px-4 py-10 sm:px-6">
        <section className="mx-auto max-w-3xl rounded-2xl border border-pd-rule bg-white p-6 shadow-sm sm:p-10">
          <h1 className="text-h3 font-display font-bold text-textPrimary">
            {definition.sourceThankYouHeading}
          </h1>
          <p className="mt-4 text-p text-textSecondary">{thankYouText}</p>
          <button
            className="mt-8 rounded-lg bg-primary px-6 py-3 font-semibold text-pd-cream hover:bg-hover-green hover:text-pd-ink"
            type="button"
            onClick={onBackToCourses}
          >
            Back
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:px-6 sm:py-12">
      <form className="mx-auto max-w-4xl" onSubmit={handleSubmit} noValidate>
        <header className="mb-8 rounded-2xl border border-pd-rule bg-white p-6 shadow-sm sm:p-10">
          <p className="text-small font-semibold uppercase tracking-wide text-primary">
            {definition.subtitle}
          </p>
          <h1 className="mt-2 text-h3 font-display font-bold text-textPrimary sm:text-h2">
            {definition.title}
          </h1>
          <div className="mt-5 space-y-3 text-p text-textSecondary">
            {definition.frontMatter.slice(0, 3).map((paragraph, index) => {
              const content = paragraph === definition.sourcePrivacyText
                ? privacyParagraph(definition.sourcePrivacyText)
                : index === 0
                  ? paragraph.replace(definition.sourceScheduleText, scheduleText)
                  : paragraph;
              return <p key={paragraph}>{content}</p>;
            })}
          </div>
          <ul className="mt-6 flex flex-wrap gap-2" aria-label="Survey details">
            {definition.frontMatter.slice(3).map((item) => (
              <li key={item} className="rounded-full bg-pd-mist px-3 py-1 text-small text-textPrimary">
                {item}
              </li>
            ))}
          </ul>
          {prefilled && (
            <p className="mt-5 rounded-lg bg-pd-mist p-4 text-small text-textPrimary">
              Your role and subject are pre-filled from your latest earlier response. You can change them.
            </p>
          )}
          {readOnly && (
            <p className="mt-5 rounded-lg border border-pd-rule p-4 text-small text-textPrimary">
              {readOnlyMessage}
            </p>
          )}
        </header>

        <section className="rounded-2xl border border-pd-rule bg-white p-6 shadow-sm sm:p-10">
          <p className="text-small font-semibold text-primary">
            Part {safePartIndex + 1} of {visibleParts.length}
          </p>
          <h2 className="mt-2 text-h4 font-display font-bold text-textPrimary">{part?.title}</h2>
          {part?.note && <p className="mt-3 text-p text-textSecondary">{part.note}</p>}
          {part?.scaleKey && (
            <p className="mt-3 rounded-lg bg-pd-mist p-3 text-small text-textPrimary">{part.scaleKey}</p>
          )}

          {error && (
            <div role="alert" className="mt-6 rounded-lg border border-pd-ink bg-pd-cream p-4 text-small text-pd-ink">
              {error}
            </div>
          )}

          <div className="mt-8 space-y-8">
            {partQuestions.map((question) => {
              const value = answers[question.id];
              const otherVisible = usesOther(question, value);
              const legend = (
                <>
                  <span className="font-semibold">{question.id.toUpperCase()}.</span>{" "}
                  {question.prompt}
                  {question.hint && <span className="ml-2 text-small font-normal text-textSecondary">({question.hint})</span>}
                </>
              );

              if (question.kind === "text") {
                return (
                  <div
                    key={question.id}
                    ref={(node) => { questionRefs.current[question.id] = node; }}
                    tabIndex={-1}
                    className="scroll-mt-6 outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {(question.id === "q24" || question.id === "q25") && (
                      <p className="mb-3 rounded-lg border border-pd-rule bg-pd-cream p-3 text-small text-pd-ink">
                        {PERSONAL_DETAILS_WARNING}
                      </p>
                    )}
                    <label className="block text-p text-textPrimary" htmlFor={question.id}>{legend}</label>
                    {question.note && <p className="mt-2 text-small text-textSecondary">{question.note}</p>}
                    <textarea
                      id={question.id}
                      className="mt-3 min-h-32 w-full rounded-lg border border-pd-rule bg-white p-3 text-p text-textPrimary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-pd-mist"
                      value={String(value ?? "")}
                      placeholder={question.placeholder}
                      disabled={readOnly}
                      onChange={(event) => updateAnswer(question, event.target.value)}
                    />
                  </div>
                );
              }

              return (
                <fieldset
                  key={question.id}
                  ref={(node) => { questionRefs.current[question.id] = node; }}
                  tabIndex={-1}
                  className="scroll-mt-6 outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <legend className="text-p text-textPrimary">{legend}</legend>
                  {question.note && <p className="mt-2 text-small text-textSecondary">{question.note}</p>}
                  <div className={question.kind === "rating" ? "mt-3 flex flex-wrap gap-2" : "mt-3 grid gap-3"}>
                    {question.options?.map((option) => {
                      const checked = Array.isArray(value)
                        ? value.some((item) => valuesEqual(item, option.value))
                        : value !== undefined && valuesEqual(value, option.value);
                      return (
                        <label
                          key={String(option.value)}
                          className="flex cursor-pointer items-start gap-3 rounded-lg border border-pd-rule px-4 py-3 text-small text-textPrimary has-[:checked]:border-primary has-[:checked]:bg-pd-mist"
                        >
                          <input
                            className="mt-0.5 h-4 w-4 accent-primary"
                            type={question.kind === "multi" ? "checkbox" : "radio"}
                            name={question.id}
                            value={String(option.value)}
                            checked={checked}
                            disabled={readOnly}
                            onChange={() => {
                              if (question.kind === "multi") {
                                const current = Array.isArray(value) ? value : [];
                                updateAnswer(
                                  question,
                                  checked
                                    ? current.filter((item) => !valuesEqual(item, option.value))
                                    : [...current, option.value],
                                );
                              } else {
                                updateAnswer(question, option.value);
                              }
                            }}
                          />
                          <span>{optionLabel(question, option.value)}</span>
                        </label>
                      );
                    })}
                  </div>
                  {otherVisible && (
                    <div className="mt-3">
                      <label className="block text-small font-semibold text-textPrimary" htmlFor={`${question.id}_other`}>
                        {question.placeholder}
                      </label>
                      <input
                        id={`${question.id}_other`}
                        className="mt-2 w-full rounded-lg border border-pd-rule bg-white p-3 text-p text-textPrimary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-pd-mist"
                        value={String(answers[`${question.id}_other`] ?? "")}
                        placeholder={question.placeholder}
                        disabled={readOnly}
                        onChange={(event) => setAnswers((current) => ({
                          ...current,
                          [`${question.id}_other`]: event.target.value,
                        }))}
                      />
                    </div>
                  )}
                </fieldset>
              );
            })}
          </div>

          <nav className="mt-10 flex flex-col-reverse gap-3 border-t border-pd-rule pt-6 sm:flex-row sm:items-center sm:justify-between" aria-label="Survey parts">
            <button
              type="button"
              className="rounded-lg border border-primary px-5 py-3 font-semibold text-primary disabled:cursor-not-allowed disabled:opacity-40"
              disabled={safePartIndex === 0 || pending}
              onClick={() => { setError(null); setPartIndex(safePartIndex - 1); }}
            >
              Previous
            </button>
            {isFinalPart ? (
              readOnly ? (
                <button
                  type="button"
                  className="rounded-lg bg-primary px-6 py-3 font-semibold text-pd-cream"
                  onClick={onBackToCourses}
                >
                  Back
                </button>
              ) : (
                <button
                  type="submit"
                  className="rounded-lg bg-primary px-6 py-3 font-semibold text-pd-cream hover:bg-hover-green hover:text-pd-ink disabled:cursor-wait disabled:opacity-60"
                  disabled={pending}
                >
                  {pending ? "Saving…" : editing ? "Save changes" : definition.sourceSubmitLabel}
                </button>
              )
            ) : (
              <button
                type="button"
                className="rounded-lg bg-primary px-6 py-3 font-semibold text-pd-cream hover:bg-hover-green hover:text-pd-ink"
                disabled={pending}
                onClick={(event) => {event.preventDefault();continueToNextPart();}}
              >
                Continue
              </button>
            )}
          </nav>
        </section>
      </form>
    </div>
  );
};

export default SurveyForm;
